import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation, useMatch } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  Snackbar,
  Switch,
  Toolbar,
  Typography,
  IconButton,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MicIcon from '@mui/icons-material/Mic';
import ListAltIcon from '@mui/icons-material/ListAlt';
import LogoutIcon from '@mui/icons-material/Logout';
import PeopleIcon from '@mui/icons-material/People';
import LockIcon from '@mui/icons-material/Lock';
import SettingsIcon from '@mui/icons-material/Settings';
import ComputerIcon from '@mui/icons-material/Computer';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ShieldIcon from '@mui/icons-material/Shield';
import DescriptionIcon from '@mui/icons-material/Description';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import StyleIcon from '@mui/icons-material/Style';
// import ArticleIcon from '@mui/icons-material/Article';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BugReportIcon from '@mui/icons-material/BugReport';
import InsightsIcon from '@mui/icons-material/Insights';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CableIcon from '@mui/icons-material/Cable';

import AppHeader from './AppHeader';
import BuildInfoPanel from './BuildInfoPanel';
import ChangePasswordDialog from './ChangePasswordDialog';
import RadiologyCopilotWidget from './RadiologyCopilotWidget';
import ReportIssueWidget from './ReportIssueWidget';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import { useAlertNotifications } from '../hooks/useAlertNotifications';
import { logout, passwordChanged } from '../store/authSlice';
import {
  selectShowTranscriptionToolbarToggles,
  toggleTranscriptionToolbarToggles,
} from '../store/uiPreferencesSlice';
import { selectDarkMode, toggleDarkMode } from '../store/userPreferencesSlice';
import { APP_NAME, COMPANY_NAME } from '../shared/app_config';
import { typography } from '~/theme';

const Layout: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [aboutDialogOpen, setAboutDialogOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'info' | 'warning' | 'error' } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user, mustChangePassword } = useAppSelector(state => state.auth);
  const isAdmin = user?.role === 'admin';

  useAlertNotifications(setToast, isAdmin);
  const transcriptionSessionId = useAppSelector(state => state.transcription.sessionId);
  const patientMatch = useMatch('/patients/:patientId');
  const currentPatientId = patientMatch?.params.patientId;
  const showToolbarToggles = useAppSelector(selectShowTranscriptionToolbarToggles);
  const darkMode = useAppSelector(selectDarkMode);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
    handleMenuClose();
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const isOnConfigPage = location.pathname === '/admin/config';

  const mainNavItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Intelligence', icon: <AutoAwesomeIcon />, path: '/intelligence' },
    { text: 'PACS Worklist', icon: <AssignmentIcon />, path: '/pacs-worklist' },
    { text: 'New Dictation', icon: <MicIcon />, path: '/transcribe' },
    { text: 'Dictation History', icon: <ListAltIcon />, path: '/transcriptions' },
    { text: 'Patient Search', icon: <PersonSearchIcon />, path: '/patients' },
    { text: 'Reporting Profiles', icon: <StyleIcon />, path: '/user/reporting-profiles' },
    // { text: 'Templates', icon: <ArticleIcon />, path: '/templates' },
    { text: 'Analytics', icon: <AssessmentIcon />, path: '/analytics' },
    ...(!isAdmin ? [{ text: 'My Issue Reports', icon: <BugReportIcon />, path: '/issue-reports' }] : []),
  ];

  // Sub-items inside the expandable "System Settings" section.
  // The `tab` value maps to the AdminConfigPage tab index.
  const settingsSubItems = [
    { text: 'System Configuration', icon: <ComputerIcon fontSize="small" />, tab: 0 },
    { text: 'Prompt Management', icon: <DescriptionIcon fontSize="small" />, tab: 1 },
    { text: 'AI & Speech Services', icon: <PsychologyIcon fontSize="small" />, tab: 2 },
    { text: 'CPT Classification', icon: <AssessmentIcon fontSize="small" />, tab: 3 },
    { text: 'Security & Compliance', icon: <ShieldIcon fontSize="small" />, tab: 4 },
    { text: 'Clinical Reporting', icon: <MedicalServicesIcon fontSize="small" />, tab: 5 },
    { text: 'SLA & Observability', icon: <MonitorHeartIcon fontSize="small" />, tab: 6 },
    { text: 'PACS Integration', icon: <CableIcon fontSize="small" />, tab: 7 },
  ];

  const handleSettingsSubNav = (tab: number) => {
    navigate(`/admin/config?tab=${tab}`);
    setDrawerOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      <AppBar position="fixed" sx={{ backgroundColor: 'background.dark' }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <AppHeader size="small" light />
          </Box>
          {user && (
            <>
              <IconButton color="inherit" onClick={handleMenuOpen} aria-label="User menu">
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                  {user.first_name[0]}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem disabled>
                  <Typography variant="body2">
                    {user.first_name} {user.last_name}
                  </Typography>
                </MenuItem>
                <MenuItem disabled>
                  <Typography variant="body2" color="text.secondary">
                    {user.role}
                  </Typography>
                </MenuItem>
                <Divider />
                <MenuItem sx={{ justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ListItemIcon sx={{ minWidth: 0 }}>
                      <TuneIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Toolbar Toggles</ListItemText>
                  </Box>
                  <Switch
                    size="small"
                    checked={showToolbarToggles}
                    onChange={() => dispatch(toggleTranscriptionToolbarToggles())}
                    sx={{ ml: 2 }}
                    inputProps={{ 'aria-label': 'Toggle transcription toolbar' }}
                  />
                </MenuItem>
                <MenuItem sx={{ justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ListItemIcon sx={{ minWidth: 0 }}>
                      {darkMode ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText>{darkMode ? 'Dark Mode' : 'Light Mode'}</ListItemText>
                  </Box>
                  <Switch
                    size="small"
                    checked={darkMode}
                    onChange={() => dispatch(toggleDarkMode())}
                    sx={{ ml: 2 }}
                    inputProps={{ 'aria-label': 'Toggle dark mode' }}
                  />
                </MenuItem>
                <MenuItem onClick={() => { navigate('/user/preferences'); handleMenuClose(); }}>
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>User Preferences</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { navigate('/user/reporting-profiles'); handleMenuClose(); }}>
                  <ListItemIcon>
                    <StyleIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Reporting Profiles</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { setAboutDialogOpen(true); handleMenuClose(); }}>
                  <ListItemIcon>
                    <InfoOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>About</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { setChangePasswordOpen(true); handleMenuClose(); }}>
                  <ListItemIcon>
                    <LockIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Change Password</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                  <ListItemText>Logout</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          '& .MuiDrawer-paper': { width: 240 },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
          <AppHeader size="medium" />
        </Box>
        <Divider />
        <Box sx={{ overflow: 'auto', flex: 1, display: 'flex', flexDirection: 'column' }}>
          <List disablePadding>
            {mainNavItems.map((item) => (
              <ListItem
                button
                key={item.text}
                onClick={() => handleNavigation(item.path)}
                sx={{ py: 1.25 }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: typography.button.fontWeight }}
                />
              </ListItem>
            ))}

            {/* ── Admin section ─────────────────────────── */}
            {isAdmin && (
              <>
                <Divider sx={{ my: 0.5 }} />
                <ListItem
                  sx={{ py: 0.25, px: 2 }}
                >
                  <Typography variant="overline" color="text.secondary" sx={{ fontSize: typography.caption.fontSize, letterSpacing: typography.overline.letterSpacing }}>
                    Administration
                  </Typography>
                </ListItem>

                {/* Expandable System Settings */}
                <ListItemButton
                  onClick={() => setSettingsOpen(!settingsOpen)}
                  selected={isOnConfigPage}
                  sx={{ py: 1 }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}><SettingsIcon /></ListItemIcon>
                  <ListItemText
                    primary="System Settings"
                    primaryTypographyProps={{ variant: 'body2', fontWeight: typography.button.fontWeight }}
                  />
                  {settingsOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {settingsSubItems.map((sub) => (
                      <ListItemButton
                        key={sub.text}
                        onClick={() => handleSettingsSubNav(sub.tab)}
                        sx={{ pl: 4, py: 0.75 }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>{sub.icon}</ListItemIcon>
                        <ListItemText
                          primary={sub.text}
                          primaryTypographyProps={{ variant: 'body2', fontSize: typography.caption.fontSize }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>

                <ListItem
                  button
                  onClick={() => handleNavigation('/admin/monitor')}
                  sx={{ py: 1.25 }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}><MonitorHeartIcon /></ListItemIcon>
                  <ListItemText
                    primary="System Monitor"
                    primaryTypographyProps={{ variant: 'body2', fontWeight: typography.button.fontWeight }}
                  />
                </ListItem>
                <ListItem
                  button
                  onClick={() => handleNavigation('/admin/users')}
                  sx={{ py: 1.25 }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}><PeopleIcon /></ListItemIcon>
                  <ListItemText
                    primary="User Administration"
                    primaryTypographyProps={{ variant: 'body2', fontWeight: typography.button.fontWeight }}
                  />
                </ListItem>
                <ListItem
                  button
                  onClick={() => handleNavigation('/admin/issue-reports')}
                  sx={{ py: 1.25 }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}><BugReportIcon /></ListItemIcon>
                  <ListItemText
                    primary="Issue Reports"
                    primaryTypographyProps={{ variant: 'body2', fontWeight: typography.button.fontWeight }}
                  />
                </ListItem>
                <ListItem
                  button
                  onClick={() => handleNavigation('/admin/issue-analytics')}
                  sx={{ py: 1.25 }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}><InsightsIcon /></ListItemIcon>
                  <ListItemText
                    primary="Issue Analytics"
                    primaryTypographyProps={{ variant: 'body2', fontWeight: typography.button.fontWeight }}
                  />
                </ListItem>
                <ListItem
                  button
                  onClick={() => handleNavigation('/admin/doctors')}
                  sx={{ py: 1.25 }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}><MedicalServicesIcon /></ListItemIcon>
                  <ListItemText
                    primary="Doctor Templates"
                    primaryTypographyProps={{ variant: 'body2', fontWeight: typography.button.fontWeight }}
                  />
                </ListItem>
              </>
            )}
          </List>
        </Box>
        <Box sx={{ py: 1, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: typography.overline.fontSize }}>
            &copy; 2026 {COMPANY_NAME}. All rights reserved.
          </Typography>
        </Box>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Toolbar sx={{ flexShrink: 0 }} />
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 2, py: 1 }}>
          <Outlet />
        </Box>
        <Box
          component="footer"
          sx={{ flexShrink: 0, textAlign: 'center', py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}
        >
          <Typography variant="caption" color="text.disabled">
            &copy; 2026 {COMPANY_NAME}. All rights reserved.
          </Typography>
        </Box>
      </Box>

      {/* AI Copilot floating widget */}
      <RadiologyCopilotWidget
        variant="fab"
        sessionId={transcriptionSessionId ?? undefined}
        patientId={currentPatientId}
      />

      {/* Issue reporting widget */}
      <ReportIssueWidget />

      {/* About / Build Info dialog */}
      <Dialog open={aboutDialogOpen} onClose={() => setAboutDialogOpen(false)} maxWidth="sm" fullWidth aria-labelledby="about-dialog-title">
        <DialogTitle id="about-dialog-title" sx={{ fontWeight: typography.h1.fontWeight }}>About {APP_NAME}</DialogTitle>
        <DialogContent>
          <BuildInfoPanel variant="expanded" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAboutDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Force password change dialog */}
      <ChangePasswordDialog
        open={mustChangePassword || changePasswordOpen}
        forced={mustChangePassword}
        onSuccess={() => {
          dispatch(passwordChanged());
          setChangePasswordOpen(false);
        }}
        onClose={() => setChangePasswordOpen(false)}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)}>
          {toast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Layout;